import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import { buildTankQrLabelSpec } from "../qr/qrLabel";
import { buildTankQrMatrix, QR_QUIET_ZONE_MODULES, QR_VERSION_1_SIZE } from "../qr/qrMatrix";

interface QrLabelPrintPanelProps {
  farmId: string;
}

interface TankRow {
  tankId: string;
  tankCode: string;
  tankFamily: string;
  isActive: boolean;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

async function loadTanks(farmId: string): Promise<TankRow[]> {
  const result = await supabase.rpc("tank_admin_list_v1", {
    target_farm_id: farmId,
  });

  if (result.error) throw result.error;

  return rows(result.data).map((item) => ({
    tankId: String(item.tank_id),
    tankCode: String(item.tank_code ?? ""),
    tankFamily: String(item.tank_family ?? ""),
    isActive: item.is_active === true,
  }));
}

function QrSvg({ tankCode }: { tankCode: string }) {
  const matrix = buildTankQrMatrix(tankCode);
  const quiet = QR_QUIET_ZONE_MODULES;
  const fullSize = QR_VERSION_1_SIZE + quiet * 2;
  const darkCells = matrix.flatMap((row, y) =>
    row.flatMap((dark, x) => (dark ? [{ id: `r${y}c${x}`, x, y }] : [])),
  );

  return (
    <svg
      className="qr-label-svg"
      viewBox={`${-quiet} ${-quiet} ${fullSize} ${fullSize}`}
      role="img"
      aria-label={`QR تانک ${tankCode}`}
      shapeRendering="crispEdges"
    >
      <rect x={-quiet} y={-quiet} width={fullSize} height={fullSize} fill="white" />
      {darkCells.map((cell) => (
        <rect key={cell.id} x={cell.x} y={cell.y} width="1" height="1" fill="black" />
      ))}
    </svg>
  );
}

export function QrLabelPrintPanel({ farmId }: QrLabelPrintPanelProps) {
  const [tanks, setTanks] = useState<TankRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [family, setFamily] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      setTanks(await loadTanks(farmId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری تانک‌ها برای چاپ QR انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visibleTanks = useMemo(() => {
    const normalizedQuery = query.trim().toUpperCase();

    return tanks.filter((tank) => {
      if (!showInactive && !tank.isActive) return false;
      if (family !== "all" && tank.tankFamily !== family) return false;
      if (normalizedQuery && !tank.tankCode.includes(normalizedQuery)) return false;
      return true;
    });
  }, [family, query, showInactive, tanks]);

  const selectedTanks = useMemo(() => {
    const selected = new Set(selectedIds);
    return tanks.filter((tank) => selected.has(tank.tankId));
  }, [selectedIds, tanks]);

  function toggleTank(tankId: string) {
    setSelectedIds((current) =>
      current.includes(tankId) ? current.filter((id) => id !== tankId) : [...current, tankId],
    );
  }

  function selectVisible() {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const tank of visibleTanks) next.add(tank.tankId);
      return [...next];
    });
  }

  function clearSelection() {
    setSelectedIds([]);
  }

  function printSelected() {
    if (selectedTanks.length === 0) {
      setError("حداقل یک تانک برای چاپ انتخاب کنید.");
      return;
    }

    setError(undefined);
    window.print();
  }

  return (
    <section className="section-card qr-label-settings" aria-labelledby="qr-label-print-title">
      <style>{`
        .qr-label-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 0.75rem;
          margin-top: 1rem;
        }

        .qr-label-card {
          box-sizing: border-box;
          width: 50mm;
          height: 50mm;
          max-width: 100%;
          aspect-ratio: 1 / 1;
          background: white;
          color: black;
          border: 1px solid #d0d0d0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1.5mm;
          break-inside: avoid;
        }

        .qr-label-svg {
          display: block;
          width: 40mm;
          height: 40mm;
          max-width: 82%;
        }

        .qr-label-code {
          direction: ltr;
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-weight: 800;
          font-size: 4.5mm;
          line-height: 1;
          letter-spacing: 0.4mm;
          margin-top: 1mm;
        }

        .qr-print-area {
          display: none;
        }

        @media print {
          @page {
            margin: 6mm;
          }

          body * {
            visibility: hidden !important;
          }

          .qr-print-area,
          .qr-print-area * {
            visibility: visible !important;
          }

          .qr-print-area {
            display: grid !important;
            position: absolute;
            inset: 0 auto auto 0;
            width: 100%;
            grid-template-columns: repeat(auto-fill, 50mm);
            grid-auto-rows: 50mm;
            gap: 2mm;
            align-content: start;
            justify-content: start;
          }

          .qr-label-card {
            width: 50mm !important;
            height: 50mm !important;
            border: 0;
            margin: 0;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4C3</p>
          <h2 id="qr-label-print-title">QR و چاپ لیبل</h2>
        </div>
        <span className="count-pill">{selectedTanks.length} انتخاب</span>
      </div>

      <p className="section-note">
        هر QR فقط کد canonical تانک را دارد. اندازه لیبل چاپی ۵۰×۵۰ میلی‌متر است و هیچ URL یا وضعیت
        متغیر سالن داخل QR قرار نمی‌گیرد.
      </p>

      <div className="operation-field-grid">
        <label>
          جستجوی کد تانک
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="مثلاً G010"
          />
        </label>

        <label>
          خانواده
          <select value={family} onChange={(event) => setFamily(event.target.value)}>
            <option value="all">همه</option>
            <option value="B">B · تکثیر</option>
            <option value="P">P · جفت</option>
            <option value="G">G · رشد</option>
            <option value="Q">Q · قرنطینه</option>
          </select>
        </label>

        <label>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />
          نمایش تانک‌های غیرفعال
        </label>
      </div>

      <div className="action-chip-list">
        <button type="button" className="secondary-button" onClick={selectVisible}>
          انتخاب همه نتایج
        </button>
        <button type="button" className="text-button" onClick={clearSelection}>
          پاک‌کردن انتخاب
        </button>
        <button
          type="button"
          className="secondary-button"
          disabled={selectedTanks.length === 0}
          onClick={printSelected}
        >
          چاپ / ذخیره PDF
        </button>
      </div>

      {loading ? <p className="section-note">در حال بارگذاری تانک‌ها…</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      <div className="work-list">
        {visibleTanks.map((tank) => (
          <label className="work-item pending" key={tank.tankId}>
            <span className="work-main">
              <strong>{tank.tankCode}</strong>
              <span>{tank.isActive ? "فعال" : "غیرفعال"}</span>
            </span>
            <input
              type="checkbox"
              checked={selectedIds.includes(tank.tankId)}
              onChange={() => toggleTank(tank.tankId)}
            />
          </label>
        ))}
      </div>

      {selectedTanks.length > 0 ? (
        <fieldset className="qr-label-preview-grid">
          <legend>پیش‌نمایش لیبل‌های انتخاب‌شده</legend>
          {selectedTanks.slice(0, 8).map((tank) => {
            const spec = buildTankQrLabelSpec(tank.tankCode);
            return (
              <div className="qr-label-card" key={tank.tankId}>
                <QrSvg tankCode={spec.encodedPayload} />
                <div className="qr-label-code">{spec.humanReadableCode}</div>
              </div>
            );
          })}
        </fieldset>
      ) : null}

      {selectedTanks.length > 8 ? (
        <p className="section-note">
          پیش‌نمایش فقط ۸ لیبل اول را نشان می‌دهد؛ چاپ شامل هر {selectedTanks.length} لیبل انتخاب‌شده
          است.
        </p>
      ) : null}

      <div className="qr-print-area" aria-hidden="true">
        {selectedTanks.map((tank) => {
          const spec = buildTankQrLabelSpec(tank.tankCode);
          return (
            <div className="qr-label-card" key={tank.tankId}>
              <QrSvg tankCode={spec.encodedPayload} />
              <div className="qr-label-code">{spec.humanReadableCode}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
